<template>
  <div class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50" @click.self="$emit('close')">
    <div class="glass-strong rounded-2xl p-6 w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto">
      <!-- Header -->
      <div class="flex items-center justify-between mb-6">
        <div>
          <h3 class="text-lg font-semibold text-white">Alert Preferences</h3>
          <p class="text-xs text-slate-400 mt-0.5">Choose how and when you get notified</p>
        </div>
        <button
          @click="$emit('close')"
          class="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Loading state -->
      <div v-if="store.loading" class="flex items-center justify-center py-12">
        <div class="w-6 h-6 border-2 border-primary-400 border-t-transparent rounded-full animate-spin"></div>
        <span class="ml-3 text-sm text-slate-400">Loading preferences…</span>
      </div>

      <!-- Form -->
      <form v-else @submit.prevent="handleSave" class="space-y-6">
        <!-- Channels Section -->
        <div>
          <h4 class="text-sm font-medium text-slate-200 mb-3">Notification Channels</h4>
          <div class="space-y-3">
            <!-- Email toggle -->
            <label class="flex items-center justify-between p-3 rounded-lg bg-slate-800/40 border border-slate-700/50 cursor-pointer hover:bg-slate-800/60 transition-colors">
              <div class="flex items-center gap-3">
                <div class="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <svg class="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <span class="text-sm text-white">Email</span>
                  <p class="text-[11px] text-slate-400">{{ form.email || 'No email set' }}</p>
                </div>
              </div>
              <div class="relative">
                <input type="checkbox" v-model="form.channel_email" class="sr-only peer" />
                <div class="w-10 h-5 rounded-full bg-slate-700 peer-checked:bg-primary-500 transition-colors"></div>
                <div class="absolute left-0.5 top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5"></div>
              </div>
            </label>

            <!-- SMS toggle -->
            <label class="flex items-center justify-between p-3 rounded-lg bg-slate-800/40 border border-slate-700/50 cursor-pointer hover:bg-slate-800/60 transition-colors">
              <div class="flex items-center gap-3">
                <div class="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <svg class="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <span class="text-sm text-white">SMS</span>
                  <p class="text-[11px] text-slate-400">Text message alerts</p>
                </div>
              </div>
              <div class="relative">
                <input type="checkbox" v-model="form.channel_sms" class="sr-only peer" />
                <div class="w-10 h-5 rounded-full bg-slate-700 peer-checked:bg-green-500 transition-colors"></div>
                <div class="absolute left-0.5 top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5"></div>
              </div>
            </label>
          </div>
        </div>

        <!-- Phone number (shown when SMS is enabled) -->
        <transition name="slide-fade">
          <div v-if="form.channel_sms">
            <label class="block text-sm font-medium text-slate-300 mb-1.5">Phone Number</label>
            <input
              v-model="form.phone"
              type="tel"
              placeholder="+15551234567"
              class="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 transition-all"
              :required="form.channel_sms"
            />
            <p class="text-[11px] text-slate-500 mt-1">
              Enter in E.164 format: <span class="text-slate-400 font-mono">+1XXXXXXXXXX</span>
              <span class="text-slate-600 ml-1">(e.g. +15551234567)</span>
            </p>
          </div>
        </transition>

        <!-- Alert Events Section -->
        <div>
          <div class="flex items-center justify-between mb-3">
            <h4 class="text-sm font-medium text-slate-200">Alert Events</h4>
            <button
              type="button"
              @click="toggleAllEvents"
              class="text-[11px] text-primary-400 hover:text-primary-300 transition-colors"
            >
              {{ allEventsSelected ? 'Deselect All' : 'Select All' }}
            </button>
          </div>
          <div class="grid grid-cols-1 gap-2">
            <label
              v-for="event in ALERT_EVENT_OPTIONS"
              :key="event.key"
              class="flex items-center gap-3 p-2.5 rounded-lg bg-slate-800/30 border border-slate-700/40 cursor-pointer hover:bg-slate-800/50 transition-colors group"
            >
              <input
                type="checkbox"
                :value="event.key"
                v-model="form.events"
                class="w-4 h-4 rounded border-slate-600 bg-slate-800 text-primary-500 focus:ring-primary-500/30 focus:ring-offset-0"
              />
              <div class="flex-1 min-w-0">
                <span class="text-sm text-white group-hover:text-white">{{ event.label }}</span>
                <p class="text-[11px] text-slate-500">{{ event.description }}</p>
              </div>
            </label>
          </div>
        </div>

        <!-- Error -->
        <div v-if="store.error" class="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
          <p class="text-xs text-red-400">{{ store.error }}</p>
        </div>

        <!-- Actions -->
        <div class="flex gap-3 pt-2">
          <button
            type="button"
            class="flex-1 px-4 py-2.5 bg-slate-800/70 border border-slate-700 text-white rounded-lg hover:bg-slate-700 transition-colors text-sm"
            @click="$emit('close')"
          >
            Cancel
          </button>
          <button
            type="submit"
            :disabled="saving"
            class="flex-1 px-4 py-2.5 rounded-lg btn-primary text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <div v-if="saving" class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            {{ saving ? 'Saving…' : 'Save Preferences' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import {
  useAlertPreferencesStore,
  ALERT_EVENT_OPTIONS,
  type AlertEventKey,
} from '@/stores/alertPreferences'
import { useAuth0Store } from '@/stores/auth0'
import { supabase } from '@/lib/supabaseClient'

const emit = defineEmits<{
  (e: 'close'): void
}>()

const store = useAlertPreferencesStore()
const auth0Store = useAuth0Store()
const saving = ref(false)

/** Local form state — populated from store on mount, written back on save. */
const form = ref({
  email: '' as string,
  phone: '' as string,
  channel_email: true,
  channel_sms: false,
  events: [] as string[],
})

const allEventsSelected = computed(
  () => form.value.events.length === ALERT_EVENT_OPTIONS.length,
)

function toggleAllEvents() {
  if (allEventsSelected.value) {
    form.value.events = []
  } else {
    form.value.events = ALERT_EVENT_OPTIONS.map((e) => e.key) as string[]
  }
}

/**
 * Fetch the user's phone number from admin_users profile.
 * Falls back gracefully if column doesn't exist or user has no phone.
 */
async function fetchProfilePhone(userId: string): Promise<string | null> {
  try {
    const { data } = await supabase
      .from('admin_users')
      .select('phone')
      .eq('user_id', userId)
      .maybeSingle()
    return (data?.phone as string) ?? null
  } catch {
    return null
  }
}

onMounted(async () => {
  // Load preferences for the current Auth0 user
  const userId = auth0Store.supabaseUserId
  const userEmail = auth0Store.user?.email
  if (userId) {
    await store.loadPreferences(userId, userEmail ?? undefined)
  }

  // Populate form from store
  if (store.preferences) {
    form.value = {
      email: store.preferences.email ?? userEmail ?? '',
      phone: store.preferences.phone ?? '',
      channel_email: store.preferences.channel_email,
      channel_sms: store.preferences.channel_sms,
      events: [...store.preferences.events],
    }
  }

  // Auto-populate phone from admin_users profile if not already set
  if (!form.value.phone && userId) {
    const profilePhone = await fetchProfilePhone(userId)
    if (profilePhone) {
      form.value.phone = profilePhone
    }
  }
})

async function handleSave() {
  saving.value = true
  try {
    await store.savePreferences({
      email: form.value.email || null,
      phone: form.value.phone || null,
      channel_email: form.value.channel_email,
      channel_sms: form.value.channel_sms,
      events: form.value.events as AlertEventKey[],
    })

    // Show success toast
    window.dispatchEvent(
      new CustomEvent('toast', {
        detail: { type: 'success', message: 'Alert preferences saved' },
      }),
    )
    emit('close')
  } catch {
    // Error is already in store.error — displayed in template
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.slide-fade-enter-active {
  transition: all 0.2s ease;
}
.slide-fade-leave-active {
  transition: all 0.15s ease;
}
.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
